import { jsx as _jsx } from "react/jsx-runtime";
import { addErrorNotification, getMemberId, loadMemberIfSignedIn, loadOrderCustomer, logger, NotificationType, orderHasInProgressAction, setOrderCustomer } from '@vista/omnichannel-components-domain';
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { useMountEffect, useUpdateEffect } from '@vista-digital/react-tools';
import { observer } from 'mobx-react';
import { forwardRef, useCallback, useMemo, useState } from 'react';
import { useFormValidator } from '../../hooks/use-form-validator';
import { Form } from '../../subcomponents/form/form/form';
import { getIdForComponentNotification } from '../../utils/get-id-for-component-notification';
import { withComponentWrapper } from '../../utils/with-component-wrapper';
import { mapFormDataToOrderCustomer } from './mappers/map-form-data-to-order-customer';
import { mapLoyaltyMemberToFormData } from './mappers/map-loyalty-member-data-to-form-data';
import { mapOrderCustomerToFormData } from './mappers/map-order-customer-to-form-data';
import { CustomerDetailsFormContextInstance } from './types/context';
import { defaultCustomerDetailsFormData } from './types/data';
import { customerDetailsFormPropsValidator, defaultCustomerDetailsFormProps } from './types/props';
const b = bemClassBuilder('v-customer-details-form');
const CustomerDetailsFormBase = (props, ref) => {
    const { formId, config, displayText, events, children } = props;
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState(defaultCustomerDetailsFormData);
    const { validationResult, validator, setValidationResult } = useFormValidator(config.validation);
    const loadCustomerDetailsFormDataFromOrderCustomer = async () => {
        const orderCustomer = await loadOrderCustomer();
        if (!orderCustomer)
            return;
        setFormData(data => (Object.assign(Object.assign({}, data), mapOrderCustomerToFormData(orderCustomer))));
        return orderCustomer;
    };
    const loadCustomerDetailsFormDataFromLoyaltyMember = async () => {
        const member = await loadMemberIfSignedIn();
        if (!member)
            return;
        setFormData(data => (Object.assign(Object.assign({}, data), mapLoyaltyMemberToFormData(member))));
    };
    const loadCustomerData = useCallback(async () => {
        setLoading(true);
        try {
            const orderCustomer = await loadCustomerDetailsFormDataFromOrderCustomer();
            if (!orderCustomer)
                await loadCustomerDetailsFormDataFromLoyaltyMember();
        }
        catch (error) {
            logger.error(error);
        }
        setLoading(false);
    }, []);
    useMountEffect(() => {
        loadCustomerData();
    });
    const memberId = getMemberId();
    // This runs only when a member signs in and attempts to load the customer data if formData is empty.
    useUpdateEffect(() => {
        (async () => {
            if (memberId && Object.values(formData).every(d => d === '')) {
                setValidationResult({ isValid: true, validationErrors: {} });
                await loadCustomerData();
            }
        })();
    }, [memberId]);
    const handleFailedSubmit = (error) => {
        var _a;
        const { errorFailedToSaveTitle, errorFailedToSaveMessage } = displayText;
        addErrorNotification({
            id: getIdForComponentNotification('customer-details-form', NotificationType.Error, 'failed-to-save'),
            messages: [errorFailedToSaveMessage],
            title: errorFailedToSaveTitle
        }, error);
        (_a = events.onAfterFailedSubmit) === null || _a === void 0 ? void 0 : _a.call(events, error);
    };
    const handleSubmit = async () => {
        const orderCustomer = mapFormDataToOrderCustomer(formData);
        await setOrderCustomer(orderCustomer);
    };
    const handleAfterSuccessfulSubmit = async () => {
        var _a;
        await ((_a = events.onAfterSuccessfulSubmit) === null || _a === void 0 ? void 0 : _a.call(events, formData));
    };
    const handleFormSubmittingStateChanged = (submitting) => {
        var _a;
        setIsSubmitting(submitting);
        (_a = events.onFormSubmittingStateChanged) === null || _a === void 0 ? void 0 : _a.call(events, submitting);
    };
    const disabled = orderHasInProgressAction() || isSubmitting;
    const context = useMemo(() => {
        return {
            onChange: (fieldName, value) => {
                setFormData(data => (Object.assign(Object.assign({}, data), { [fieldName]: value })));
            },
            formData,
            validationResult,
            validationConfig: config.validation,
            validator,
            disabled,
            loading
        };
    }, [formData, validationResult, config.validation, validator, disabled, loading]);
    return (_jsx(Form, Object.assign({}, props, { id: formId, className: b.block(), validator: () => validator.validateForm(formData), events: Object.assign(Object.assign({}, events), { onSubmit: handleSubmit, onAfterSuccessfulSubmit: handleAfterSuccessfulSubmit, onAfterFailedSubmit: handleFailedSubmit, onFormSubmittingStateChanged: handleFormSubmittingStateChanged }), isLoading: loading, setFormData: setFormData, ref: ref }, { children: _jsx(CustomerDetailsFormContextInstance.Provider, Object.assign({ value: context }, { children: children })) })));
};
const CustomerDetailsForm = observer(forwardRef(CustomerDetailsFormBase));
CustomerDetailsForm.displayName = 'CustomerDetailsForm';
/**
 * The **Customer Details Form** component allows customer details to be specified for an in-progress order.
 */
export const CustomerDetailsFormComponent = withComponentWrapper(CustomerDetailsForm, defaultCustomerDetailsFormProps, customerDetailsFormPropsValidator);
